import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { titleCase } from '../util/index.js';
import { assertCan } from '../util/permissions.js';
import { Status } from '../util/schemas.js';
import { addEventLog } from './util.js';
export const WardSchema = z.object({
    status: Status,
    name: z.string(),
    code: z.string(),
    locationId: z.uuidv7(),
    sectionId: z.uuidv7(),
    costCenterNumber: z.string(),
    orderNumber: z.string(),
    movementType: z.string(),
});
export const AddWardSchema = WardSchema;
export const UpdateWardSchema = AddWardSchema.partial().extend({ id: z.uuidv7() });
export const UpdateWardBatchStatusSchema = z.object({ wardIds: z.array(z.uuidv7()), status: Status });
export const DeleteWardBatchSchema = z.object({ wardIds: z.array(z.uuidv7()) });
export const wards = {
    add: defineMutator(AddWardSchema, async ({ args: ward, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        if (ward.code) {
            const existingWards = await tx.run(zql.wards.where('code', '=', ward.code).one());
            if (existingWards)
                throw new Error('Ward code already exists');
        }
        if (ward.costCenterNumber) {
            const existingWards = await tx.run(zql.wards.where('costCenterNumber', '=', ward.costCenterNumber).one());
            if (existingWards)
                throw new Error('Ward cost center already exists');
        }
        const newWard = {
            id: uuidv7(),
            name: titleCase(ward.name),
            code: ward.code,
            locationId: ward.locationId,
            sectionId: ward.sectionId,
            costCenterNumber: ward.costCenterNumber ?? null,
            orderNumber: ward.orderNumber || '',
            movementType: ward.movementType || '',
            status: ward.status ?? 'active',
            createdAt: Date.now(),
            createdBy: ctx.user.id,
        };
        await tx.mutate.wards.insert(newWard);
        if (tx.location === 'server') {
            const patches = createPatch({}, newWard);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'ward',
                resourceId: newWard.id,
                action: 'create',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: Date.now(),
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: newWard.id,
                    name: newWard.name,
                },
            }, ctx);
        }
    }),
    update: defineMutator(UpdateWardSchema, async ({ args: ward, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const prev = await tx.run(zql.wards.where('id', '=', ward.id).one());
        if (!prev)
            throw new Error('Not Found');
        if (ward.code) {
            const existingWards = await tx.run(zql.wards.where('code', '=', ward.code).where('id', '!=', ward.id).one());
            if (existingWards)
                throw new Error('Ward code already exists');
        }
        if (ward.costCenterNumber) {
            const existingWards = await tx.run(zql.wards.where('costCenterNumber', '=', ward.costCenterNumber).where('id', '!=', ward.id).one());
            if (existingWards)
                throw new Error('Ward cost center already exists');
        }
        const now = Date.now();
        await tx.mutate.wards.update({
            ...ward,
            id: prev.id,
            name: titleCase(ward.name),
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
        if (tx.location === 'server') {
            const patches = createPatch(prev, { ...prev, ...ward, updatedAt: now, updatedBy: ctx.user.id });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'ward',
                resourceId: prev.id,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: ward.id,
                    name: ward.name,
                },
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateWardBatchStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const wards = await tx.run(zql.wards.where('id', 'IN', args.wardIds));
        //TODO: Check if the ward has any patients or batches of products
        const now = Date.now();
        await Promise.all(wards.map(async (ward) => {
            await tx.mutate.wards.update({
                id: ward.id,
                status: args.status,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
        }));
        if (tx.location === 'server') {
            await Promise.all(wards.map(async (ward) => {
                const patches = createPatch(ward, { ...ward, status: args.status, updatedAt: now, updatedBy: ctx.user.id });
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'ward',
                    resourceId: ward.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                    domain: 'configuration',
                    details: {
                        id: ward.id,
                        name: ward.name,
                        ...itemsDetail(statusItems(ward.status, args.status)),
                    },
                }, ctx);
            }));
        }
    }),
    deleteBatch: defineMutator(DeleteWardBatchSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const wards = await tx.run(zql.wards.where('id', 'IN', args.wardIds));
        //TODO: Check if the ward has any patients or batches of products
        const now = Date.now();
        await Promise.all(wards.map(async (ward) => {
            await tx.mutate.wards.update({
                id: ward.id,
                deletedAt: now,
                deletedBy: ctx.user.id,
                // update code and costCenterNumber so we can use it for another ward
                code: `${ward.code}_deleted_${now}`,
                costCenterNumber: `${ward.costCenterNumber}_deleted_${now}`,
            });
        }));
        if (tx.location === 'server') {
            await Promise.all(wards.map(async (ward) => {
                const patches = createPatch(ward, { ...ward, deletedAt: now, deletedBy: ctx.user.id });
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'ward',
                    resourceId: ward.id,
                    action: 'delete',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        id: ward.id,
                        name: ward.name,
                    },
                }, ctx);
            }));
        }
    }),
};
//# sourceMappingURL=ward.mutators.js.map